import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { dateToLocalISO } from '@/utils/date';

/**
 * Convert a dateRange preset + optional custom start/end into ISO YYYY-MM-DD boundaries.
 * Identical logic to ReportsPage so date filtering behaves the same everywhere.
 *
 * Jul 2026 — uses `dateToLocalISO` (local calendar day) instead of the naive
 * `.toISOString()` UTC pattern, otherwise Sydney (UTC+10) users would see
 * date ranges shifted 1 day earlier before 10 AM local time.
 */
export const getDateBoundaries = (range, customStart = '', customEnd = '') => {
  const today = new Date();
  const todayISO = dateToLocalISO(today);
  const toISO = (d) => dateToLocalISO(d);
  if (range === 'last7') {
    const w = new Date(today); w.setDate(today.getDate() - 7);
    return { start: toISO(w), end: todayISO };
  }
  if (range === 'last30') {
    const m = new Date(today); m.setDate(today.getDate() - 30);
    return { start: toISO(m), end: todayISO };
  }
  if (range === 'this_month') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start: toISO(first), end: todayISO };
  }
  if (range === 'last_month') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: toISO(first), end: toISO(last) };
  }
  if (range === 'this_quarter') {
    const q = Math.floor(today.getMonth() / 3);
    const first = new Date(today.getFullYear(), q * 3, 1);
    return { start: toISO(first), end: todayISO };
  }
  if (range === 'ytd') {
    const first = new Date(today.getFullYear(), 0, 1);
    return { start: toISO(first), end: todayISO };
  }
  if (range === 'custom' && customStart && customEnd) {
    return { start: customStart, end: customEnd };
  }
  return { start: '', end: '' };
};

const PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Range' },
];

/**
 * DateRangePicker — pill-button row + optional custom date inputs.
 * Matches the layout used on the Reports page so the whole app feels uniform.
 */
export const DateRangePicker = ({
  dateRange,
  setDateRange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  label = 'Date Range',
}) => {
  const bounds = useMemo(() => getDateBoundaries(dateRange, startDate, endDate), [dateRange, startDate, endDate]);
  return (
    <div>
      <Label>{label}</Label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.4rem' }}>
        {PRESETS.map((p) => (
          <Button
            key={p.value}
            variant={dateRange === p.value ? 'default' : 'outline'}
            onClick={() => setDateRange(p.value)}
            data-testid={`date-range-${p.value.replace(/_/g, '-')}`}
            size="sm"
          >
            {p.label}
          </Button>
        ))}
      </div>
      {dateRange === 'custom' && (
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            <Label htmlFor="custom-start" style={{ fontSize: '0.8rem' }}>Start</Label>
            <Input id="custom-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="custom-end" style={{ fontSize: '0.8rem' }}>End</Label>
            <Input id="custom-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
      )}
      {dateRange !== 'all' && bounds.start && bounds.end && (
        <p style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#64748b' }}>
          Showing {bounds.start} to {bounds.end}
        </p>
      )}
    </div>
  );
};

export default DateRangePicker;
